import { Amplify } from 'aws-amplify';
import {
    confirmSignIn,
    fetchAuthSession,
    getCurrentUser,
    setUpTOTP,
    signIn,
    signInWithRedirect,
    signOut,
    verifyTOTPSetup,
    type ConfirmSignInInput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import QRCode from 'qrcode';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import amplifyConfig from '../lib/amplify-config';
import { AuthContext } from './auth-context';
import { type AuthContextType, type User } from './types';

Amplify.configure(amplifyConfig);

interface AuthProviderProps {
    children: ReactNode;
}

const extractHubError = (data: unknown): unknown => {
    if (data !== null && typeof data === 'object' && 'error' in data) {
        return data.error;
    }
    return undefined;
};

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingMFASetup, setPendingMFASetup] = useState(false);

    const loadUser = useCallback(async () => {
        try {
            setLoading(true);
            const [currentUser, session] = await Promise.all([getCurrentUser(), fetchAuthSession()]);
            const accessToken = session.tokens?.accessToken;
            const groups = (accessToken?.payload['cognito:groups'] as string[] | undefined) ?? [];
            const loginId = currentUser.signInDetails?.loginId;

            setUser({
                email: loginId ?? '',
                userId: currentUser.userId,
                groups,
                mfaEnabled: false,
            });
            setError(null);
        } catch {
            setUser(null);
            setError(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadUser();
    }, [loadUser]);

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                case 'signInWithRedirect':
                    void loadUser();
                    break;
                case 'signInWithRedirect_failure': {
                    const err = extractHubError(payload.data);
                    setError(err instanceof Error ? err.message : 'Google sign-in failed');
                    break;
                }
            }
        });
        return () => {
            unsubscribe();
        };
    }, [loadUser]);

    const login = useCallback(
        async (email: string, password: string) => {
            try {
                setError(null);
                const signInResult = await signIn({ username: email, password });

                if (signInResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
                    setPendingMFASetup(true);
                    throw new Error('MFA_SETUP_REQUIRED');
                }

                if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                    throw new Error('MFA_CODE_REQUIRED');
                }

                if (signInResult.isSignedIn) {
                    await loadUser();
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Login failed';
                setError(message);
                throw err;
            }
        },
        [loadUser],
    );

    const signInWithGoogle = useCallback(async () => {
        try {
            setError(null);
            await signInWithRedirect({ provider: 'Google' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Google sign-in failed';
            setError(message);
            throw err;
        }
    }, []);

    const setupMFA = useCallback(async (): Promise<string> => {
        try {
            setError(null);
            const totpSetup = await setUpTOTP();
            const setupUri = totpSetup.getSetupUri('SlateAdminPortal');
            const qrCodeUrl = await QRCode.toDataURL(setupUri.href);
            return qrCodeUrl;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'MFA setup failed';
            setError(message);
            throw err;
        }
    }, []);

    const verifyMFA = useCallback(
        async (code: string) => {
            try {
                setError(null);

                if (pendingMFASetup) {
                    await verifyTOTPSetup({ code });
                    const confirmInput: ConfirmSignInInput = {
                        challengeResponse: code,
                    };
                    await confirmSignIn(confirmInput);
                    setPendingMFASetup(false);
                } else {
                    const confirmInput: ConfirmSignInInput = {
                        challengeResponse: code,
                    };
                    await confirmSignIn(confirmInput);
                }

                await loadUser();
            } catch (err) {
                const message = err instanceof Error ? err.message : 'MFA verification failed';
                setError(message);
                throw err;
            }
        },
        [pendingMFASetup, loadUser],
    );

    const logout = useCallback(async () => {
        try {
            setError(null);
            await signOut();
            setUser(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Logout failed';
            setError(message);
            throw err;
        }
    }, []);

    const refreshSession = useCallback(async () => {
        try {
            await loadUser();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Session refresh failed';
            setError(message);
            throw err;
        }
    }, [loadUser]);

    const value: AuthContextType = {
        user,
        loading,
        error,
        login,
        signInWithGoogle,
        setupMFA,
        verifyMFA,
        logout,
        refreshSession,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
